import React, { useMemo, useState } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  FormLabel,
} from '@chakra-ui/react';

/**
 * Selector múltiple de modelos con búsqueda por texto.
 */
const ModelMultiSearchSelect = ({
  models = [],
  selectedIds = [],
  onChange,
  label = 'Modelos de vehículo',
  placeholder = 'Escriba para buscar modelo…',
  isDisabled = false,
  hint,
  showAllModelsTag = true,
  allModelsLabel = 'Todos los modelos',
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const labelById = useMemo(() => {
    const map = {};
    models.forEach((m) => {
      const brand = m.brand?.name ? ` (${m.brand.name})` : '';
      map[Number(m.id)] = `${m.description || ''}${brand}`.trim();
    });
    return map;
  }, [models]);

  const selectedSet = useMemo(
    () => new Set(selectedIds.map((id) => Number(id))),
    [selectedIds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return models.filter((m) => {
      if (selectedSet.has(Number(m.id))) return false;
      if (!q) return true;
      return String(m.description || '')
        .toLowerCase()
        .includes(q);
    });
  }, [models, query, selectedSet]);

  const addModel = (modelId) => {
    const id = Number(modelId);
    if (!id || selectedSet.has(id)) return;
    onChange([...selectedIds.map(Number), id]);
    setQuery('');
    setOpen(false);
  };

  const removeModel = (modelId) => {
    const id = Number(modelId);
    onChange(selectedIds.map(Number).filter((x) => x !== id));
  };

  const showDropdown =
    open && !isDisabled && (query.length > 0 || filtered.length > 0);

  const isAllModels = selectedIds.length === 0;

  const selectAllModels = () => {
    if (isDisabled || isAllModels) return;
    onChange([]);
    setQuery('');
    setOpen(false);
  };

  return (
    <Box>
      {label && (
        <FormLabel fontSize="sm" mb={1} fontWeight="bold">
          {label}
        </FormLabel>
      )}
      {hint && (
        <Text fontSize="sm" color="gray.600" mb={2}>
          {hint}
        </Text>
      )}

      {(showAllModelsTag || selectedIds.length > 0) && (
        <Wrap spacing={2} mb={2}>
          {showAllModelsTag ? (
            <WrapItem>
              <Tag
                size="md"
                colorScheme={isAllModels ? 'green' : 'gray'}
                variant={isAllModels ? 'solid' : 'outline'}
                borderRadius="full"
                cursor={!isDisabled && !isAllModels ? 'pointer' : 'default'}
                onClick={selectAllModels}
              >
                <TagLabel>{allModelsLabel}</TagLabel>
              </Tag>
            </WrapItem>
          ) : null}
          {selectedIds.map((id) => (
            <WrapItem key={id}>
              <Tag size="md" colorScheme="blue" borderRadius="full">
                <TagLabel>{labelById[Number(id)] || `Modelo #${id}`}</TagLabel>
                {!isDisabled && (
                  <TagCloseButton onClick={() => removeModel(id)} />
                )}
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <Box position="relative">
        <Input
          size="sm"
          placeholder={placeholder}
          value={query}
          isDisabled={isDisabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
        {showDropdown && (
          <List
            position="absolute"
            zIndex={20}
            w="100%"
            mt={1}
            bg="white"
            borderWidth="1px"
            borderRadius="md"
            maxH="220px"
            overflowY="auto"
            boxShadow="md"
          >
            {filtered.length === 0 ? (
              <ListItem px={3} py={2} fontSize="sm" color="gray.500">
                Sin resultados
              </ListItem>
            ) : (
              filtered.map((m) => (
                <ListItem
                  key={m.id}
                  px={3}
                  py={2}
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  onMouseDown={() => addModel(m.id)}
                >
                  {m.description}
                  {m.brand?.name ? ` (${m.brand.name})` : ''}
                </ListItem>
              ))
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ModelMultiSearchSelect;
